import React, { useEffect, useState } from 'react';
import { useGetExocetFamilies } from '../../../../hooks/datarefs';
import PagePaper from '../../../Tools/Pages/PagePaper';
import { Grid, IconButton } from '@mui/material';
import {
  BirdzDialog,
  BirdzNotif,
  StyledTitle,
  useDialog,
  useNotif
} from '@applications-terrains/birdz-react-library';
import BSearchForm from '../../../Tools/Search/BSearchForm';
import BTable from '../../../Tools/Table/BTable';
import useRefresh from '../../../../hooks/useRefresh';
import TrueFalse from '../../../Tools/TrueFalse';
import { AddCircle } from '@mui/icons-material';
import ModalWithLoader, {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import axios from 'axios';
import { Edit } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import InterventionTypesForm from './InterventionTypesForm';
import { useTranslation } from 'react-i18next';

export default function InterventionTypes() {
  const [id, setId] = useState<string | null>(null);
  const endpoint = '/api/boi/exocet/types/';
  const { data: families } = useGetExocetFamilies();
  const [filters, setFilters] = useState<string>('');
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const { notif, notifOptions } = useNotif();
  const { refresh, triggerRefresh } = useRefresh();
  const [action, setAction] = useState<ModalWithLoaderAction>(null);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(null);

  const { t } = useTranslation();
  const translateType = (value: string) => {
    switch (value) {
      case 'EligibilityAudit':
        return t('intervention.audit');
      case 'AttributeInstall':
        return t('intervention.installAttribute');
      case 'AttributeMaintenance':
        return t('intervention.maintenanceAttribute');
      case 'MeterInstall':
        return t('intervention.installMeter');
      case 'MeterMaintenance':
        return t('intervention.maintenanceMeter');
      case 'DeviceInstall':
        return t('intervention.installDevice');
      case 'DeviceDump':
        return t('intervention.removeDevice');
      case 'DeviceReinstall':
        return t('intervention.reinstallDevice');
      case 'DeviceMaintenance':
        return t('intervention.maintenanceDevice');
      case 'RepeaterMaintenance':
        return t('intervention.maintenanceRepeater');
      case 'VisualIndex':
        return t('intervention.visualRead');
      case 'RadioIndex':
        return t('intervention.radioRead');
      case 'DeviceUpdate':
        return t('intervention.deviceUpdate');
      case 'RepeaterUpdate':
        return t('intervention.repeaterUpdate');
      case 'MeterMaintenanceWithCS':
        return t('intervention.maintenanceMeterWithSurvey');
      case 'DeviceMaintenanceWithCS':
        return t('intervention.maintenanceDeviceWithSurvey');
      case 'MeterInstallWithCS':
        return t('intervention.installMeterWithSurvey');
      case 'DeviceInstallWithCS':
        return t('intervention.installDeviceWithSurvey');
      case 'ConnectionSurveyWithCS':
        return t('intervention.connectionSurvey');
      case 'DeviceInstallLight':
        return t('intervention.installDeviceLight');
      case 'DeviceMaintenanceLight':
        return t('intervention.maintenanceDeviceLight');
      case 'RadioIndexRead':
        return t('intervention.unitaryRadioRead');
      case 'QuickUpdate':
        return t('intervention.quickUpdate');
      default:
        return value;
    }
  };

  const translateFamily = (family: string) => {
    switch (family) {
      case 'WaterMeter':
        return t('intervention.waterMeter');
      case 'Repetition':
        return t('intervention.repetitionEquipment');
      case 'FirePost':
        return t('intervention.firePost');
      case 'WaterHydrant':
        return t('intervention.waterHydrant');
      case 'LeakDetection':
        return t('intervention.leakDetection');
      case 'PAV':
        return t('intervention.pav');
      case 'LeakPreLocator':
        return t('intervention.leakPreLocator');
      case 'LuKa':
        return t('intervention.luka');
      case 'API-BI':
        return t('intervention.apiBi');
      default:
        return family;
    }
  };

  const moduleFamilyTranslations: Record<string, string> = {
    WaterMeter: t('intervention.waterMeter'),
    Repetition: t('intervention.repetitionEquipment'),
    FirePost: t('intervention.firePost'),
    WaterHydrant: t('intervention.waterHydrant'),
    LeakDetection: t('intervention.leakDetection'),
    PAV: t('intervention.pav'),
    LeakPreLocator: t('intervention.leakPreLocator'),
    LuKa: t('intervention.luka'),
    'API-BI': t('intervention.apiBi')
  };

  const moduleFamilyOptions = families
    ? families?.map((family) => ({
        value: family.Id,
        label: moduleFamilyTranslations[family.Name] ?? family.Name
      })) || []
    : [];

  const handleCloseModalWithLoader = () => {
    setAction(null);
    setRequestStatus(null);
    setId(null);
  };

  useEffect(() => {
    if (requestStatus === 'success') {
      triggerRefresh();
      setId(null);
    }
  }, [requestStatus, triggerRefresh]);

  const searchFields: any[] = [
    {
      name: 'families',
      label: t('equipmentConfiguration.equipmentFamilies'),
      multiple: true,
      options: {
        values: moduleFamilyOptions
      }
    }
  ];

  const listFields = [
    { name: 'id', label: t('fields.code'), orderable: true },
    {
      name: 'name',
      label: t('fields.interventionType'),
      transform: (value: any) => {
        return translateType(value);
      },
      orderable: true
    },
    {
      name: 'families',
      label: t('fields.equipmentFamily'),
      transform: (values: any) => {
        const familiesName: string[] = [];
        families?.map((family) => {
          values?.forEach((value: number) => {
            if (family.Id === value) {
              const name = translateFamily(family.Name);
              return familiesName.push(name);
            }
          });
        });
        return familiesName.join(', ');
      },
      orderable: true
    },
    {
      name: 'configuration_required',
      label: t('fields.required_configuration'),
      transform: (value: boolean) => {
        return <TrueFalse value={value} />;
      },
      orderable: true
    },
    { name: 'duration', label: t('fields.duration'), orderable: true }
  ];

  const actions: any[] = [
    {
      name: 'edit',
      label: t('interventionTypes.editType'),
      render: (item: any) => {
        return (
          <IconButton
            onClick={() => {
              setAction('edit');
              setId(item.id);
            }}
          >
            <Edit fontSize="small" color="action" />
          </IconButton>
        );
      }
    },
    {
      name: 'delete',
      label: t('interventionTypes.deleteType'),
      render: (param: any) => {
        return (
          <IconButton
            onClick={() => {
              confirmDialog({
                title: t('interventionTypes.deleteType'),
                content: t('interventionTypes.deleteTypeContent'),
                onValidate: () => {
                  setAction('delete');
                  axios.delete(`/api/boi/exocet/types/${param.id}/`).then(
                    () => {
                      setAction(null);
                      triggerRefresh();
                      notif({
                        content: t('interventionTypes.deleteTypeSuccess'),
                        type: 'success'
                      });
                    },
                    () => {
                      setAction(null);
                      notif({
                        content: t('interventionTypes.deleteError'),
                        type: 'error'
                      });
                    }
                  );
                  closeDialog();
                },
                onCancel: () => {
                  closeDialog();
                }
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      }
    }
  ];

  return (
    <PagePaper
      loader={listFields.length === 0}
      title={
        <Grid justifyContent="space-between" alignItems={'center'} container>
          <Grid item>
            <StyledTitle className="m-0">{t('interventionTypes.title')}</StyledTitle>
          </Grid>
        </Grid>
      }
    >
      <BSearchForm columns={1} fields={searchFields} setSearchString={setFilters} />
      <BTable
        model={'intervention_types'}
        endpoint={endpoint + filters}
        defaultOrder={'id'}
        fields={listFields}
        actions={actions}
        refresh={refresh}
        headerActions={[
          {
            name: t('interventionTypes.addTitle'),
            element: (
              <IconButton onClick={() => setAction('add')}>
                <AddCircle fontSize="small" color="action" />
              </IconButton>
            )
          }
        ]}
      />
      <BirdzDialog options={dialogOptions} />
      <BirdzNotif options={notifOptions} />

      <ModalWithLoader
        openModal={action === 'add' || action === 'edit'}
        onClose={() => handleCloseModalWithLoader()}
        onSuccess={() => handleCloseModalWithLoader()}
        action={
          action === 'delete'
            ? t('interventionTypes.deleteTypeAction')
            : action === 'edit'
              ? t('interventionTypes.editTypeAction')
              : t('interventionTypes.createTypection')
        }
        status={requestStatus}
        setStatus={setRequestStatus}
        showCloseButton={true}
      >
        <InterventionTypesForm
          endpoint={endpoint}
          setRequestStatus={setRequestStatus}
          action={action}
          setAction={setAction}
          id={id}
        />
      </ModalWithLoader>
    </PagePaper>
  );
}
